import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { EpgChannel } from '@jwp/ott-common/types/epg';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { getJustFinishedPlaylistFromChannels, getLivePlaylistFromChannels, getSoonToStartPlaylistFromChannels } from '@jwp/ott-common/src/utils/epg';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';

import Shelf from '../../components/Shelf/Shelf';
import shelfListStyles from '../../containers/ShelfList/ShelfList.module.scss'; // Reuse to prevent duplicate styling
import shelfStyles from '../../components/Shelf/Shelf.module.scss'; // Reuse to prevent duplicate styling
import CardGrid from '../CardGrid/CardGrid';

import styles from './EpgLiveNow.module.scss';

// Shelf containers are needed for padding and chevron button positioning
const Section = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <section className={shelfListStyles.shelfContainer} aria-label={title}>
    {children}
  </section>
);

type Props = {
  channels: EpgChannel[];
  getProgramUrl: (channelId: string, programId: string) => string | undefined;
  className?: string;
  isFetching: boolean;
};

const EpgLiveNow: React.FC<Props> = ({ channels, className, isFetching, getProgramUrl }: Props) => {
  const { t } = useTranslation('epg');
  const { accessModel } = useConfigStore(({ accessModel }) => ({ accessModel }), shallow);
  const { hasSubscription, isLoggedIn } = useAccountStore(({ user, subscription }) => ({ hasSubscription: !!subscription, isLoggedIn: !!user }), shallow);

  const liveNowPrograms = useMemo(() => getLivePlaylistFromChannels(t('live_now'), channels), [t, channels]);
  const soonToStartPrograms = useMemo(() => getSoonToStartPlaylistFromChannels(t('scheduled'), channels), [t, channels]);
  const justFinishedPrograms = useMemo(() => getJustFinishedPlaylistFromChannels(t('replay'), channels), [t, channels]);

  const hasSoonToStartPrograms = soonToStartPrograms.playlist.length > 0;
  const hasJustFinishedPrograms = justFinishedPrograms.playlist.length > 0;

  const getCardUrl = (item: PlaylistItem) => {
    const { channelId, programId } = item.metadata as { channelId: string; programId: string };
    return getProgramUrl(channelId, programId) || '';
  };

  if (isFetching) {
    return <LoadingOverlay />;
  }

  if (!hasSoonToStartPrograms && !hasJustFinishedPrograms) {
    return (
      <div className={classNames(styles.epgLiveNow, className)}>
        <Section title={t('live_now')}>
          <h2 className={shelfStyles.title}>{t('live_now')}</h2>
          <CardGrid
            getUrl={getCardUrl}
            playlist={liveNowPrograms}
            isLoading={false}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
          />
        </Section>
      </div>
    );
  }

  return (
    <div className={classNames(styles.epgLiveNow, className)}>
      <Section title={t('live_now')}>
        <Shelf
          playlist={liveNowPrograms}
          type="playlist"
          accessModel={accessModel}
          isLoggedIn={isLoggedIn}
          hasSubscription={hasSubscription}
          getUrl={getCardUrl}
        />
      </Section>
      {hasSoonToStartPrograms && (
        <Section title={t('scheduled')}>
          <Shelf
            playlist={soonToStartPrograms}
            type="playlist"
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
            getUrl={getCardUrl}
          />
        </Section>
      )}
      {hasJustFinishedPrograms && (
        <Section title={t('replay')}>
          <Shelf
            playlist={justFinishedPrograms}
            type="playlist"
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
            getUrl={getCardUrl}
          />
        </Section>
      )}
    </div>
  );
};

export default EpgLiveNow;
